import React, { useEffect, useMemo, useState } from 'react';
import { Truck, Building2, MapPin, Save, CheckSquare, Square, Search, RefreshCw, ChevronDown, ChevronRight, Filter, Info, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { useToast } from '../components/ui/Toast';
import { useOrders } from '../contexts/OrderContext';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { filterTodayOrders } from '../utils/dateFilters';
// --- Types ---
interface AssignedBranch {
  companyId: string | number;
  companyName: string;
  branchId: string;
  branchName: string;
}
interface AdminUser {
  id: string;
  username: string;
  name: string;
  email: string;
  role: string;
  permissions: {
    role?: string;
    assignedBranches?: AssignedBranch[];
    // Legacy fields (cleared on save)
    assignedCompanies?: (string | number)[];
    assignedOrderIds?: string[];
    [key: string]: any;
  };
}
interface Branch {
  id: string;
  name: string;
}
interface Company {
  id: string | number;
  name: string;
  branches: Branch[];
}
// --- Helpers ---
const formatDate = (date: Date) => {
  return date.toLocaleDateString('es-DO', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};
export function AssignmentsPage() {
  useDocumentTitle('Asignación de Entregas');
  const {
    orders,
    refreshOrders
  } = useOrders();
  const {
    showToast
  } = useToast();
  const {
    addLog
  } = useActivityLog();
  // --- State ---
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Selection State
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  // Store selected branches as composite keys: "companyId|branchId"
  const [selectedBranchKeys, setSelectedBranchKeys] = useState<Set<string>>(new Set());
  // UI State
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string | number>>(new Set());
  // --- Data Loading ---
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [adminUsers, companiesData] = await Promise.all([supabaseClient.getAdminUsers(), isSupabaseConfigured() ? supabaseClient.getCompanies() : []]);
        // Filter for delivery users
        const deliveryUsers = (adminUsers || []).filter((u: any) => u.role === 'delivery' || u.permissions?.role === 'delivery').sort((a: any, b: any) => a.name.localeCompare(b.name));
        setUsers(deliveryUsers);
        setCompanies(companiesData || []);
        // Auto-select first user if available
        if (deliveryUsers.length > 0 && !selectedUserId) {
          handleSelectUser(deliveryUsers[0].id, deliveryUsers[0]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        showToast('error', 'Error', 'No se pudieron cargar los datos');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);
  // Auto-refresh orders
  useEffect(() => {
    if (refreshOrders) {
      const interval = setInterval(refreshOrders, 10000);
      return () => clearInterval(interval);
    }
  }, [refreshOrders]);
  // --- Derived Data ---
  const todayOrders = useMemo(() => {
    return filterTodayOrders(orders).filter(o => o.status !== 'Cancelado' && o.status !== 'Entregado');
  }, [orders]);
  // Calculate order counts per branch
  const branchOrderCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    todayOrders.forEach(order => {
      // Create a key based on company name and branch name
      // Note: We match by name because orders might not have IDs
      const key = `${order.company}|${order.branch || 'Principal'}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [todayOrders]);
  // Filter companies for display
  const displayedCompanies = useMemo(() => {
    if (!companySearchTerm) return companies;
    const term = companySearchTerm.toLowerCase();
    return companies.filter(c => c.name.toLowerCase().includes(term));
  }, [companies, companySearchTerm]);
  // Stats
  const totalAssignedBranches = selectedBranchKeys.size;
  const totalOrdersCovered = useMemo(() => {
    let count = 0;
    // Iterate all selected branches and sum their order counts
    selectedBranchKeys.forEach(key => {
      const [companyId, branchId] = key.split('|');
      const company = companies.find(c => String(c.id) === companyId);
      if (company) {
        const branch = company.branches.find(b => b.id === branchId);
        // Fallback for companies with no branches defined but having orders
        const branchName = branch ? branch.name : 'Principal';
        const orderKey = `${company.name}|${branchName}`;
        count += branchOrderCounts[orderKey] || 0;
      }
    });
    return count;
  }, [selectedBranchKeys, companies, branchOrderCounts]);
  // --- Handlers ---
  const handleSelectUser = (userId: string, userData?: AdminUser) => {
    const user = userData || users.find(u => u.id === userId);
    if (!user) return;
    setSelectedUserId(userId);
    // Load existing assignments into state
    const currentAssignments = user.permissions?.assignedBranches || [];
    const keys = new Set<string>();
    currentAssignments.forEach(a => {
      keys.add(`${a.companyId}|${a.branchId}`);
    });
    setSelectedBranchKeys(keys);
  };
  const toggleBranch = (companyId: string | number, branchId: string) => {
    const key = `${companyId}|${branchId}`;
    setSelectedBranchKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };
  const toggleCompany = (company: Company) => {
    const branches = company.branches.length > 0 ? company.branches : [{
      id: 'default',
      name: 'Principal'
    }]; // Handle companies with no explicit branches
    const allKeys = branches.map(b => `${company.id}|${b.id}`);
    const allSelected = allKeys.every(key => selectedBranchKeys.has(key));
    setSelectedBranchKeys(prev => {
      const next = new Set(prev);
      if (allSelected) {
        // Deselect all
        allKeys.forEach(key => next.delete(key));
      } else {
        // Select all
        allKeys.forEach(key => next.add(key));
      }
      return next;
    });
  };
  const toggleExpandCompany = (companyId: string | number) => {
    setExpandedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };
  const handleSave = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      const user = users.find(u => u.id === selectedUserId);
      if (!user) return;
      // Reconstruct the assignedBranches array
      const newAssignments: AssignedBranch[] = [];
      selectedBranchKeys.forEach(key => {
        const [companyId, branchId] = key.split('|');
        const company = companies.find(c => String(c.id) === companyId);
        if (company) {
          const branch = company.branches.find(b => b.id === branchId);
          // Handle virtual default branch
          const branchName = branch ? branch.name : 'Principal';
          newAssignments.push({
            companyId: company.id,
            companyName: company.name,
            branchId: branchId,
            branchName: branchName
          });
        }
      });
      // Update permissions
      const updatedPermissions = {
        ...user.permissions,
        role: 'delivery',
        assignedBranches: newAssignments,
        // Clear legacy/conflicting fields to ensure clean state
        assignedCompanies: [],
        assignedOrderIds: []
      };
      await supabaseClient.updateAdminUser(selectedUserId, {
        permissions: updatedPermissions
      });
      // Update local state
      setUsers(prev => prev.map(u => u.id === selectedUserId ? {
        ...u,
        permissions: updatedPermissions
      } : u));
      addLog('Asignación Delivery', `Asignadas ${newAssignments.length} sucursales a ${user.name}`, 'employee');
      showToast('success', 'Cambios guardados', `Se asignaron ${newAssignments.length} sucursales a ${user.name}`);
    } catch (error) {
      console.error('Error saving:', error);
      showToast('error', 'Error', 'No se pudieron guardar los cambios');
    } finally {
      setSaving(false);
    }
  };
  return <div className="flex flex-col h-[calc(100vh-64px)] bg-gray-50 dark:bg-gray-950 overflow-hidden" data-id="element-1520">
      {/* Header Bar */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-4 flex flex-col sm:flex-row items-center justify-between gap-4 z-20 shadow-sm" data-id="element-1521">
        <div className="flex items-center gap-4 w-full sm:w-auto" data-id="element-1522">
          <div className="flex items-center gap-2" data-id="element-1523">
            <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg text-blue-600 dark:text-blue-400" data-id="element-1524">
              <Truck size={20} data-id="element-1525" />
            </div>
            <div data-id="element-1526">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-none" data-id="element-1527">
                Asignación de Entregas
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-1528">
                {formatDate(new Date())}
              </p>
            </div>
          </div>

          <div className="h-8 w-px bg-gray-200 dark:bg-gray-800 mx-2 hidden sm:block" data-id="element-1529"></div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-id="element-1530">
            <div className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs font-medium border border-gray-200 dark:border-gray-700" data-id="element-1531">
              Sucursales:{' '}
              <span className="font-bold text-gray-900 dark:text-white" data-id="element-1532">
                {totalAssignedBranches}
              </span>
            </div>
            <div className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-full text-xs font-medium text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800" data-id="element-1533">
              Pedidos cubiertos:{' '}
              <span className="font-bold" data-id="element-1534">{totalOrdersCovered}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-1535">
          <Button variant="outline" size="sm" onClick={() => refreshOrders?.()} leftIcon={<RefreshCw size={14} data-id="element-1537" />} data-id="element-1536">
            Actualizar
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!selectedUserId || saving} leftIcon={<Save size={14} data-id="element-1539" />} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-id="element-1538">
            {saving ? 'Guardando...' : 'Guardar Asignación'}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden" data-id="element-1540">
        {/* Left Sidebar: Delivery Users */}
        <div className="w-full sm:w-80 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col z-10" data-id="element-1541">
          <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-1542">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-1543">
              Repartidores
            </h3>
            <div className="relative" data-id="element-1544">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} data-id="element-1545" />
              <input type="text" placeholder="Buscar repartidor..." className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1546" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1" data-id="element-1547">
            {users.map(user => {
            const isSelected = user.id === selectedUserId;
            // Calculate branch count (use local state if selected, otherwise saved state)
            const branchCount = isSelected ? selectedBranchKeys.size : user.permissions?.assignedBranches?.length || 0;
            // Workload indicator
            const workloadColor = branchCount > 10 ? 'bg-red-500' : branchCount > 5 ? 'bg-yellow-500' : 'bg-green-500';
            return <button key={user.id} onClick={() => handleSelectUser(user.id, user)} className={`w-full text-left p-3 rounded-xl transition-all duration-200 flex items-center gap-3 border ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 shadow-sm' : 'hover:bg-gray-50 dark:hover:bg-gray-800 border-transparent'}`} data-id="element-1548">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`} data-id="element-1549">
                    {user.name.charAt(0).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-1550">
                    <p className={`text-sm font-medium truncate ${isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900 dark:text-white'}`} data-id="element-1551">
                      {user.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1" data-id="element-1552">
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-1553">
                        <div className={`h-full rounded-full ${workloadColor}`} style={{
                      width: `${Math.min(branchCount / 15 * 100, 100)}%`
                    }} data-id="element-1554"></div>
                      </div>
                      <span className="text-xs font-mono font-medium text-gray-500 dark:text-gray-400" data-id="element-1555">
                        {branchCount}
                      </span>
                    </div>
                  </div>

                  {isSelected && <ChevronRight size={16} className="text-blue-500" data-id="element-1556" />}
                </button>;
          })}

            {users.length === 0 && !loading && <div className="p-4 text-center text-gray-500 text-sm" data-id="element-1557">
                No hay repartidores disponibles
              </div>}
          </div>
        </div>

        {/* Right Panel: Companies & Branches */}
        <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-950 overflow-hidden" data-id="element-1558">
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center gap-3" data-id="element-1559">
            <SearchBar placeholder="Buscar empresa..." onSearch={setCompanySearchTerm} className="flex-1" data-id="element-1560" />
            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-lg" data-id="element-1561">
              <Info size={14} data-id="element-1562" />
              <span data-id="element-1563">Selecciona las sucursales para asignar</span>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-id="element-1564">
            {!selectedUserId ? <div className="flex flex-col items-center justify-center h-full text-center opacity-60" data-id="element-1565">
                <Truck size={64} className="text-gray-300 dark:text-gray-600 mb-4" data-id="element-1566" />
                <h3 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1567">
                  Selecciona un Repartidor
                </h3>
                <p className="text-gray-500 max-w-xs" data-id="element-1568">
                  Elige un usuario del panel izquierdo para comenzar a asignar
                  sucursales.
                </p>
              </div> : displayedCompanies.length === 0 ? <div className="text-center py-12 opacity-60" data-id="element-1569">
                <Building2 size={48} className="mx-auto text-gray-300 mb-4" data-id="element-1570" />
                <p className="text-gray-500" data-id="element-1571">No se encontraron empresas</p>
              </div> : displayedCompanies.map(company => {
            const isExpanded = expandedCompanies.has(company.id);
            // Handle companies with no explicit branches by creating a virtual one
            const branches = company.branches && company.branches.length > 0 ? company.branches : [{
              id: 'default',
              name: 'Principal'
            }];
            // Check how many branches are selected
            const selectedCount = branches.filter(b => selectedBranchKeys.has(`${company.id}|${b.id}`)).length;
            const allSelected = selectedCount === branches.length && branches.length > 0;
            // Calculate total orders for this company
            const companyTotalOrders = branches.reduce((sum, b) => {
              return sum + (branchOrderCounts[`${company.name}|${b.name}`] || 0);
            }, 0);
            return <div key={company.id} className={`bg-white dark:bg-gray-900 rounded-xl border shadow-sm transition-all ${selectedCount > 0 ? 'border-blue-200 dark:border-blue-800 ring-1 ring-blue-100 dark:ring-blue-900' : 'border-gray-200 dark:border-gray-800'}`} data-id="element-1572">
                    {/* Company Header */}
                    <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors rounded-t-xl" onClick={() => toggleExpandCompany(company.id)} data-id="element-1573">
                      <div className="flex items-center gap-3" data-id="element-1574">
                        <div className={`p-1 rounded-md transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-1575">
                          <ChevronDown size={18} className="text-gray-400" data-id="element-1576" />
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1577">
                          <Building2 size={18} className={selectedCount > 0 ? 'text-blue-500' : 'text-gray-400'} data-id="element-1578" />
                          <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-1579">
                            {company.name}
                          </h3>
                          {companyTotalOrders > 0 && <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs px-2 py-0.5 rounded-full font-bold" data-id="element-1580">
                              {companyTotalOrders} pedidos
                            </span>}
                        </div>
                      </div>

                      <div className="flex items-center gap-3" data-id="element-1581">
                        {selectedCount > 0 && <span className="text-xs font-medium text-blue-600 dark:text-blue-400" data-id="element-1582">
                            {selectedCount}/{branches.length} sucursales
                          </span>}
                        <button onClick={e => {
                    e.stopPropagation();
                    toggleCompany(company);
                  }} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${allSelected ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'}`} data-id="element-1583">
                          {allSelected ? 'Desmarcar Todo' : 'Asignar Todo'}
                        </button>
                      </div>
                    </div>

                    {/* Branch List */}
                    <AnimatePresence data-id="element-1584">
                      {isExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50" data-id="element-1585">
                          <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-1586">
                            {branches.map(branch => {
                      const key = `${company.id}|${branch.id}`;
                      const isChecked = selectedBranchKeys.has(key);
                      const orderCount = branchOrderCounts[`${company.name}|${branch.name}`] || 0;
                      return <div key={branch.id} onClick={() => toggleBranch(company.id, branch.id)} className={`
                                    flex items-center justify-between p-3 rounded-lg cursor-pointer border transition-all
                                    ${isChecked ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800' : 'bg-white border-gray-200 hover:border-gray-300 dark:bg-gray-800 dark:border-gray-700'}
                                  `} data-id="element-1587">
                                  <div className="flex items-center gap-3" data-id="element-1588">
                                    <div className={`text-gray-400 ${isChecked ? 'text-blue-600' : ''}`} data-id="element-1589">
                                      {isChecked ? <CheckSquare size={20} data-id="element-1590" /> : <Square size={20} data-id="element-1591" />}
                                    </div>
                                    <div data-id="element-1592">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1593">
                                        {branch.name}
                                      </p>
                                      <p className="text-xs text-gray-500 flex items-center gap-1" data-id="element-1594">
                                        <MapPin size={10} data-id="element-1595" />
                                        Sucursal
                                      </p>
                                    </div>
                                  </div>

                                  {orderCount > 0 && <span className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-700 rounded-md border border-gray-100 dark:border-gray-600 shadow-sm" data-id="element-1596">
                                      <span className="w-2 h-2 rounded-full bg-green-500" data-id="element-1597"></span>
                                      <span className="text-xs font-bold text-gray-700 dark:text-gray-200" data-id="element-1598">
                                        {orderCount}
                                      </span>
                                    </span>}
                                </div>;
                    })}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
}